class FreeShippingMeter extends HTMLElement {
    constructor() {
        super();
        this.handleCartChange = this.handleCartChange.bind(this);
        this.requestId = 0;
    }

    connectedCallback() {
        this.progressBar = this.querySelector('[data-shipping-progress]');
        this.messageElement = this.querySelector('[data-shipping-message]');
        this.progressMeter = this.querySelector('[data-free-shipping-progress-meter]');
        this.progressElement = this.querySelector('[role="progressbar"]');
        this.textEnabled = this.progressBar?.dataset.textEnabled === 'true';
        this.shipVal = this.getCopy('free_shipping_1', '');

        if (!this.progressBar || !this.messageElement || !this.progressMeter) {
            this.setInvalidState();
            return;
        }

        this.removeEventListener('change', this.handleCartChange);
        this.addEventListener('change', this.handleCartChange);
        this.initialize();
    }

    disconnectedCallback() {
        this.removeEventListener('change', this.handleCartChange);
    }

    getCopy(key, fallback) {
        const copy = window.free_shipping_text || {};
        return typeof copy[key] === 'string' ? copy[key] : fallback;
    }

    getThreshold() {
        const configuredValue = this.dataset.freeShippingThreshold || window.free_shipping_price;
        const threshold = Number(configuredValue);

        return Number.isFinite(threshold) && threshold > 0 ? threshold : null;
    }

    handleCartChange(event) {
        const cart = event?.detail?.cart;

        if (cart && Number.isFinite(Number(cart.total_price))) {
            this.updateFromCart(cart);
            return;
        }

        this.initialize();
    }

    updateFromCart(cart) {
        if (!cart || !Number.isFinite(Number(cart.total_price))) return false;

        ++this.requestId;
        this.cart = cart;
        this.calculateProgress(cart);
        return true;
    }

    initialize() {
        const requestId = ++this.requestId;

        if (!window.Shopify || typeof Shopify.getCart !== 'function') {
            this.setInvalidState();
            return;
        }

        Shopify.getCart((cart) => {
            if (requestId !== this.requestId || !this.isConnected) return;

            this.cart = cart;
            this.calculateProgress(cart);
        });
    }

    getEligibleCartTotal(cart) {
        let totalPrice = Number(cart?.total_price);

        if (!Number.isFinite(totalPrice)) return null;
        totalPrice = Math.max(0, totalPrice);

        if (this.dataset.freeShippingExcludeGiftCards !== 'true') return totalPrice;

        const cartGiftCardTotal = Array.isArray(cart.items)
            ? cart.items.reduce((total, item) => {
                if (item?.gift_card !== true) return total;

                const linePrice = Number(item.final_line_price ?? item.line_price ?? 0);
                return Number.isFinite(linePrice) ? total + Math.max(0, linePrice) : total;
            }, 0)
            : 0;

        if (cartGiftCardTotal > 0) return Math.max(0, totalPrice - cartGiftCardTotal);

        const itemSelector = this.closest('#halo-cart-sidebar')
            ? '.previewCartItem[data-price-gift-card]'
            : '.cart-item[data-price-gift-card]';
        const domGiftCardTotal = Array.from(document.querySelectorAll(itemSelector)).reduce((total, item) => {
            const linePrice = Number(item.dataset.priceGiftCard);
            return Number.isFinite(linePrice) ? total + Math.max(0, linePrice) : total;
        }, 0);

        return Math.max(0, totalPrice - domGiftCardTotal);
    }

    calculateProgress(cart) {
        const threshold = this.getThreshold();
        const totalPrice = this.getEligibleCartTotal(cart);

        if (threshold === null || totalPrice === null) {
            this.setInvalidState();
            return;
        }

        const thresholdInCents = Math.round(threshold * 100);
        if (thresholdInCents <= 0) {
            this.setInvalidState();
            return;
        }

        const progress = Math.min(100, Math.max(0, (totalPrice / thresholdInCents) * 100));
        const state = totalPrice === 0 ? 'empty' : totalPrice >= thresholdInCents ? 'unlocked' : 'locked';
        const remainingPrice = Math.max(0, thresholdInCents - totalPrice);
        const text = this.getText(state, thresholdInCents, remainingPrice);

        this.render({ progress, state, text, thresholdInCents, totalPrice });
    }

    getText(state, thresholdInCents, remainingPrice) {
        if (state === 'empty') {
            return '<span>' + this.getCopy('free_shipping_message', 'Free shipping over') + ' ' + this.formatMoney(thresholdInCents) + '!</span>';
        }

        if (state === 'unlocked') {
            this.shipVal = this.getCopy('free_shipping_1', '');
            return this.getCopy('free_shipping_message_1', 'Free shipping unlocked.');
        }

        this.shipVal = this.getCopy('free_shipping_2', '');
        return '<span>' + this.getCopy('free_shipping_message_2', 'Spend') + ' </span>'
            + this.formatMoney(remainingPrice)
            + '<span> ' + this.getCopy('free_shipping_message_3', 'more to') + ' </span>'
            + '<span class="text">' + this.getCopy('free_shipping_message_4', 'unlock free shipping.') + '</span>';
    }

    formatMoney(valueInCents) {
        if (typeof Shopify.formatMoney === 'function') {
            return Shopify.formatMoney(valueInCents, window.money_format);
        }

        return '$' + (valueInCents / 100).toFixed(2);
    }

    getClassLabel(progress, state) {
        if (state === 'unlocked') return 'progress-free';
        if (progress === 0) return 'none';
        if (progress <= 30) return 'progress-30';
        if (progress <= 60) return 'progress-60';
        return 'progress-100';
    }

    resetProgressClass(classLabel) {
        this.progressBar.classList.remove('none', 'progress-30', 'progress-60', 'progress-100', 'progress-free');
        this.progressBar.classList.add(classLabel);
    }

    render({ progress, state, text, thresholdInCents, totalPrice }) {
        this.hidden = false;
        this.style.setProperty('display', 'block');
        this.dataset.freeShippingState = state;
        this.dataset.freeShippingThresholdCents = String(thresholdInCents);
        this.dataset.freeShippingCartTotalCents = String(totalPrice);
        this.resetProgressClass(this.getClassLabel(progress, state));

        this.progressBar.classList.toggle('progress-hidden', state === 'empty');
        this.progressMeter.style.width = `${progress}%`;
        this.messageElement.innerHTML = text;
        this.messageElement.classList.toggle('progress-free', state === 'unlocked');

        if (this.textEnabled) {
            const textWrapper = this.progressMeter.querySelector('.text');
            if (textWrapper) textWrapper.textContent = `${progress.toFixed(2)}%`;
        }

        if (this.progressElement) {
            this.progressElement.setAttribute('aria-valuenow', String(Math.round(progress)));
            this.progressElement.setAttribute('aria-valuetext', this.messageElement.textContent.replace(/\s+/g, ' ').trim());
        }

        this.convertCurrencyIfNeeded();
        this.dispatchEvent(new CustomEvent('free-shipping:updated', {
            bubbles: true,
            detail: { state, progress, thresholdInCents, totalPrice }
        }));
    }

    setInvalidState() {
        this.dataset.freeShippingState = 'invalid';
        this.hidden = true;
        this.style.setProperty('display', 'none', 'important');
        this.dispatchEvent(new CustomEvent('free-shipping:updated', {
            bubbles: true,
            detail: { state: 'invalid' }
        }));
    }

    convertCurrencyIfNeeded() {
        const $currency = window.jQuery;
        const CurrencyAPI = window.Currency;
        const shopCurrency = window.shop_currency || window.shopCurrency;

        if (!$currency || !shopCurrency || !CurrencyAPI || typeof CurrencyAPI.convertAll !== 'function') return;
        if (!$currency('.dropdown-item[data-currency]').length) return;

        if ((window.show_multiple_currencies && CurrencyAPI.currentCurrency !== shopCurrency) || window.show_auto_currency) {
            CurrencyAPI.convertAll(shopCurrency, $currency('#currencies .active').attr('data-currency'), 'span.money', 'money_format');
        }
    }
}

if (!customElements.get('free-shipping-component')) {
    customElements.define('free-shipping-component', FreeShippingMeter);
}
